import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Car, ChevronDown, ChevronUp,
  Calendar, User, Package, RefreshCw, AlertTriangle, Phone
} from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import { getAllBookings, getAllInvoices } from '../api';
import './CarHistoryPage.css';

const INR = n => '₹' + Number(n || 0).toLocaleString('en-IN');

/**
 * Groups completed bookings by registration number.
 * Returns an array of vehicle objects each with their full service history.
 */
function groupByVehicle(bookings) {
  const map = {};
  (bookings || []).forEach(b => {
    const reg = b.regNumber || b.reg || 'UNKNOWN';
    if (!map[reg]) {
      map[reg] = {
        reg,
        make:     b.vehicleMake  || '',
        model:    b.vehicleModel || '',
        year:     b.vehicleYear  || '',
        owner:    b.ownerName    || b.customer || '—',
        phone:    b.phone        || '—',
        records:  [],
      };
    }
    map[reg].records.push(b);
  });

  return Object.values(map).map(v => {
    // Sort records newest first
    v.records.sort((a, b) => {
      const da = a.date ? new Date(a.date) : new Date(0);
      const db = b.date ? new Date(b.date) : new Date(0);
      return db - da;
    });
    const totalSpend = v.records.reduce((s, r) => {
      // grandTotal comes from merged invoice; fall back to estimatedAmount on booking
      const amt = Number(r.grandTotal || r.estimatedAmount || 0);
      return s + amt;
    }, 0);
    return {
      ...v,
      totalServices: v.records.length,
      totalSpend,
      lastService: v.records[0]?.date ? String(v.records[0].date) : '—',
    };
  }).sort((a, b) => {
    // Sort vehicles by most recent service
    if (a.lastService === '—') return 1;
    if (b.lastService === '—') return -1;
    return new Date(b.lastService) - new Date(a.lastService);
  });
}

export default function CarHistoryPage() {
  const [vehicles,  setVehicles]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [expanded,  setExpanded]  = useState(null);

  /* Load ALL bookings + ALL invoices in parallel, merge by bookingId */
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [all, invoices] = await Promise.all([
        getAllBookings(),
        getAllInvoices().catch(() => []),
      ]);

      // Build lookup: bookingId → invoice
      const invMap = {};
      (invoices || []).forEach(inv => {
        if (inv.bookingId) invMap[inv.bookingId] = inv;
      });

      // Merge invoice data into each booking
      const enriched = (all || []).map(b => {
        const inv = invMap[b.id] || invMap[b.invoiceId] || null;
        if (!inv) return b;
        return {
          ...b,
          grandTotal:    inv.grandTotal    || 0,
          subtotal:      inv.subtotal      || 0,
          gstAmount:     inv.gstAmount     || 0,
          gstPercent:    inv.gstPercent    || 18,
          labourCharges: inv.labourCharges || 0,
          services:      inv.services?.length
                           ? inv.services.map(s => typeof s === 'object' ? s.name : s)
                           : b.services,
          spareParts:    inv.spareParts    || b.spareParts || [],
          mechanicName:  inv.mechanicName  || b.mechanicName || '',
          invoiceStatus: inv.status,
          invoicePaid:   inv.status === 'paid' || inv.status === 'PAID',
          invoiceId:     inv.id || b.invoiceId,
          serviceLines:  inv.services  || [],
          partLines:     inv.spareParts || [],
        };
      });

      setVehicles(groupByVehicle(enriched));
    } catch (e) {
      setError('Failed to load history. Check backend connection.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Search: reg, owner, make, model, phone */
  const filtered = vehicles.filter(v => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [v.reg, v.owner, v.make, v.model, v.phone]
      .join(' ').toLowerCase().includes(q);
  });

  const getService = b => Array.isArray(b.services) ? b.services.join(', ') : (b.service || '—');
  
  const getBreakdown = b => {
    if (!b.subtotal && !b.grandTotal) return null;
    return {
      subtotal:  Number(b.subtotal      || 0),
      gst:       Number(b.gstAmount     || 0),
      labour:    Number(b.labourCharges || 0),
      grand:     Number(b.grandTotal    || 0),
      gstPct:    b.gstPercent || 18,
    };
  };
  const getMech  = b => b.mechanicName || b.mechanic || '—';
  const getParts = b => b.partLines?.length ? b.partLines : (b.spareParts || []);

  return (
    <>
      <TopBar title="Car Service History" subtitle="Complete vehicle history from database"/>
      <div className="admin-content">
        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>Vehicle History</h1>
            <p>
              {loading ? 'Loading…'
                : `${vehicles.length} vehicle${vehicles.length !== 1 ? 's' : ''} · ${vehicles.reduce((s,v)=>s+v.totalServices,0)} completed services`}
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spinning' : ''}/> Refresh
          </button>
        </div>

        {/* Search */}
        <div style={{ position:'relative', maxWidth:480, marginBottom:24 }}>
          <Search size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none' }}/>
          <input className="input" style={{ paddingLeft:36 }}
            placeholder="Search by reg number, owner, make or model…"
            value={search} onChange={e => setSearch(e.target.value)}/>
        </div>

        {/* States */}
        {loading && (
          <div style={{ textAlign:'center', padding:'60px 0', color:'var(--text-muted)' }}>
            <div style={{ width:32,height:32,borderRadius:'50%',border:'3px solid var(--border)',borderTopColor:'var(--accent)',animation:'spin 0.8s linear infinite',margin:'0 auto 12px' }}/>
            <p style={{ fontSize:13 }}>Loading vehicle history from database…</p>
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign:'center', padding:'48px 0', color:'var(--danger)' }}>
            <AlertTriangle size={36} style={{ marginBottom:12, opacity:.7 }}/>
            <p>{error}</p>
            <button className="btn btn-outline btn-sm" style={{ marginTop:12 }} onClick={load}>Retry</button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:'60px 24px', color:'var(--text-muted)' }}>
            <Car size={40} style={{ marginBottom:12, opacity:.4 }}/>
            <p>{vehicles.length === 0
              ? 'No completed services yet — history appears here when bookings are marked completed.'
              : 'No vehicles match your search.'}</p>
          </div>
        )}

        {/* Vehicle cards */}
        {!loading && !error && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {filtered.map(v => {
              const isOpen = expanded === v.reg;
              return (
                <div key={v.reg} className={`vehicle-card card ${isOpen ? 'vehicle-card--open' : ''}`}>
                  {/* Vehicle header */}
                  <div className="vehicle-card__header" onClick={() => setExpanded(isOpen ? null : v.reg)}>
                    <div className="vehicle-card__icon"><Car size={20}/></div>
                    <div className="vehicle-card__info">
                      <div className="vehicle-card__title">
                        <span className="vehicle-card__name">
                          {[v.year, v.make, v.model].filter(Boolean).join(' ') || 'Vehicle'}
                        </span>
                        <span className="vehicle-card__reg">{v.reg}</span>
                      </div>
                      <div className="vehicle-card__meta">
                        <span><User size={11}/> {v.owner}</span>
                        {v.phone !== '—' && <span><Phone size={11}/> {v.phone}</span>}
                        <span><Calendar size={11}/> Last: {v.lastService}</span>
                      </div>
                    </div>
                    <div className="vehicle-card__stats">
                      <div className="vehicle-stat">
                        <span className="vehicle-stat__val">{v.totalServices}</span>
                        <span className="vehicle-stat__lbl">Bookings</span>
                      </div>
                      <div className="vehicle-stat">
                        <span className="vehicle-stat__val">{v.totalSpend > 0 ? INR(v.totalSpend) : '—'}</span>
                        <span className="vehicle-stat__lbl">Total Billed</span>
                      </div>
                      <div className="vehicle-stat">
                        <span className="vehicle-stat__val" style={{ color:'var(--success)' }}>
                          {v.records.filter(r => r.invoicePaid).length}
                        </span>
                        <span className="vehicle-stat__lbl">Paid</span>
                      </div>
                    </div>
                    <button className="tracker-expand-btn" style={{ marginLeft:16 }}>
                      {isOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                    </button>
                  </div>

                  {/* Service records table */}
                  {isOpen && (
                    <div className="vehicle-records af">
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Booking ID</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Service</th>
                              <th>Mechanic</th>
                              <th>Spare Parts</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {v.records.map(r => (
                              <tr key={r.id}>
                                <td>
                                  <span style={{ fontWeight:700, color:'var(--accent)', fontSize:12 }}>{r.id}</span>
                                </td>
                                <td style={{ whiteSpace:'nowrap', fontSize:12 }}>
                                  {r.date ? String(r.date) : '—'}
                                </td>
                                <td>
                                  {(() => {
                                    const s = (r.status || '').toLowerCase().replace(/[_-]/g,'');
                                    const cfg =
                                      s.includes('complet')                                    ? { label:'Completed',  color:'var(--success)',    bg:'rgba(22,163,74,0.10)' } :
                                      s.includes('inprogress')||s.includes('inservice')        ? { label:'In Service', color:'#2563eb',           bg:'rgba(37,99,235,0.10)' } :
                                      s.includes('confirmed')                                  ? { label:'Confirmed',  color:'#7c3aed',           bg:'rgba(124,58,237,0.10)' } :
                                      s.includes('cancel')                                     ? { label:'Cancelled',  color:'var(--text-muted)', bg:'var(--bg-secondary)' } :
                                      s.includes('reject')                                     ? { label:'Rejected',   color:'var(--danger)',     bg:'rgba(220,38,38,0.10)' } :
                                                                                                 { label:'Pending',    color:'var(--warning)',    bg:'rgba(217,119,6,0.10)' };
                                    return (
                                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:99, background:cfg.bg, color:cfg.color, whiteSpace:'nowrap' }}>
                                        {cfg.label}
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td style={{ fontSize:13 }}>{getService(r)}</td>
                                <td>
                                  {getMech(r) !== '—' ? (
                                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                      <div style={{ width:26, height:26, borderRadius:'50%', background:'var(--accent-glow)', border:'1px solid var(--border-accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'var(--accent)', flexShrink:0 }}>
                                        {getMech(r).split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                                      </div>
                                      <span style={{ fontSize:13 }}>{getMech(r)}</span>
                                    </div>
                                  ) : <span style={{ fontSize:12, color:'var(--text-muted)' }}>—</span>}
                                </td>
                                <td>
                                  {getParts(r).length > 0 ? (
                                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                                      {getParts(r).map((p, i) => {
                                        const name     = typeof p === 'object' ? (p.name || '—')      : p;
                                        const qty      = typeof p === 'object' ? (p.qty  || p.quantity || '') : '';
                                        const rate     = typeof p === 'object' ? (p.unitPrice || p.rate || 0) : 0;
                                        const lineTotal= typeof p === 'object' ? (p.lineTotal || (qty && rate ? qty * rate : 0)) : 0;
                                        return (
                                          <div key={i} style={{ display:'flex', flexDirection:'column', gap:1 }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                                              <Package size={9} color="var(--accent)"/>
                                              <span style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>{name}</span>
                                            </div>
                                            {(qty || rate > 0) && (
                                              <span style={{ fontSize:11, color:'var(--text-muted)', paddingLeft:14 }}>
                                                {qty ? `Qty: ${qty}` : ''}{qty && rate > 0 ? '  ·  ' : ''}{rate > 0 ? `${INR(rate)}/unit` : ''}{lineTotal > 0 ? `  =  ${INR(lineTotal)}` : ''}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : <span style={{ fontSize:11, color:'var(--text-muted)' }}>—</span>}
                                </td>
                                <td>
                                  {(() => {
                                    const bd = getBreakdown(r);
                                    if (!bd) return (
                                      <span style={{ fontSize:12, color:'var(--text-muted)' }}>
                                        {r.estimatedAmount ? INR(r.estimatedAmount) : '—'}
                                      </span>
                                    );
                                    return (
                                      <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:130 }}>
                                        {bd.subtotal > 0 && (
                                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-muted)' }}>
                                            <span>Subtotal</span><span>{INR(bd.subtotal)}</span>
                                          </div>
                                        )}
                                        {bd.labour > 0 && (
                                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-muted)' }}>
                                            <span>Labour</span><span>{INR(bd.labour)}</span>
                                          </div>
                                        )}
                                        {bd.gst > 0 && (
                                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-muted)' }}>
                                            <span>GST ({bd.gstPct}%)</span><span>{INR(bd.gst)}</span>
                                          </div>
                                        )}
                                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, fontWeight:700, color:'var(--accent)', borderTop:'1px solid var(--border)', paddingTop:3, marginTop:1 }}>
                                          <span>Total</span><span>{INR(bd.grand)}</span>
                                        </div>
                                        {/* Payment status */}
                                        <span style={{
                                          fontSize:10, fontWeight:700, padding:'2px 7px',
                                          borderRadius:99, width:'fit-content', marginTop:2,
                                          background: r.invoicePaid ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.08)',
                                          color: r.invoicePaid ? 'var(--success)' : 'var(--danger)',
                                        }}>
                                          {r.invoicePaid ? '✓ Paid' : '✗ Unpaid'}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spinning{animation:spin .8s linear infinite}`}</style>
    </>
  );
}